import {useState} from "react";
import {Table, Tag, Alert, Row, Col, DatePicker, Collapse} from "antd";
import {Link} from "react-router-dom";
import dayjs from "dayjs";
import {PageHeader, Loading, FontAwesomeIcon, ExportButtons} from "~/components";
import {useGetAdminStatsQuery} from "~/reduxs/api/adminStatsApiSlice";
import {nf, compact, planColor} from "../util";
import style from "../style/Member.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

/** Huy hiệu % thay đổi so kỳ trước. null = "mới"; màu theo good/bad (login-fail đảo). */
function DeltaBadge({pct, goodWhenUp = true}) {
	if (pct === null || pct === undefined) return <Tag color="blue" style={{marginLeft: 6}}>mới</Tag>;
	if (pct === 0) return <span style={{marginLeft: 6, color: '#9ca3af', fontSize: 12}}>0%</span>;
	const up = pct > 0;
	const good = goodWhenUp ? up : !up;
	return (
		<span style={{marginLeft: 6, color: good ? '#16a34a' : '#dc2626', fontSize: 12, fontWeight: 600}}>
			{up ? '▲' : '▼'} {Math.abs(pct)}%
		</span>
	);
}

/** Thẻ chỉ số nhỏ (tái dùng lớp .stat của Member.module.scss); có delta tuỳ chọn. */
function StatCard({icon, value, label, delta, goodWhenUp = true}) {
	return (
		<div className={cn('stat')}>
			<div className={cn('stat-icon')}><FontAwesomeIcon icon={icon} /></div>
			<div className={cn('stat-body')}>
				<div className={cn('stat-value')}>
					{value}
					{delta !== undefined && <DeltaBadge pct={delta} goodWhenUp={goodWhenUp} />}
				</div>
				<div className={cn('stat-label')}>{label}</div>
			</div>
		</div>
	);
}

function AdminStats() {
	// Khoảng ngày cho khối "Hoạt động trong kỳ" (mặc định 30 ngày gần nhất).
	const [range, setRange] = useState([dayjs().subtract(29, 'day'), dayjs()]);
	const rangeParams = range && range[0] && range[1]
		? {from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD')}
		: {};
	const {data, isLoading, isError} = useGetAdminStatsQuery(rangeParams);

	if (isError) {
		return (
			<div className="container">
				<PageHeader icon="fa-light fa-chart-line" title="Thống kê tổng quan" />
				<Alert type="error" showIcon message="Không tải được số liệu thống kê." />
			</div>
		);
	}

	if (isLoading || !data) {
		return (
			<div className="container">
				<PageHeader icon="fa-light fa-chart-line" title="Thống kê tổng quan" />
				<Loading />
			</div>
		);
	}

	const {members, plans, resources, ai_tokens, ai_budget, url_checks, activity, top_token_users, top_project_users, alerts} = data;
	const am = activity?.metrics || {};
	const recentFails = alerts?.recent_fails || [];

	// Dữ liệu xuất báo cáo (Excel/PDF) — gate theo cờ gói ở ExportButtons.
	const overviewRows = [
		['Tổng thành viên', nf(members.total)],
		['Đang hoạt động', nf(members.active)],
		['Bị khóa / tạm dừng', nf(members.blocked)],
		['Mới trong tháng', nf(members.new_this_month)],
		['Doanh thu ước tính / tháng', `${nf(plans?.revenue_monthly)}đ`],
		['Giá vốn ước tính / tháng', `${nf(plans?.cost_monthly)}đ`],
		['Lợi nhuận ước tính / tháng', `${nf(plans?.profit_monthly)}đ`],
		['Cần giữ trong tài khoản AI', `${nf(ai_budget?.recommended_vnd)}đ (≈ $${ai_budget?.recommended_usd ?? 0})`],
		['Token AI dùng trong tháng', nf(ai_tokens.used_this_month)],
		['Kiểm tra URL trong tuần', nf(url_checks.used_this_week)],
		['Tổng dự án', nf(resources.projects)],
	];
	const planRows = (plans?.items || []).map((p) => [
		p.name, p.price > 0 ? `${nf(p.price)}đ` : 'Miễn phí', nf(p.subscribers), `${nf(p.revenue)}đ`,
		`${nf(p.cost_vnd)}đ`, `${nf(p.profit_vnd)}đ`, `${nf(p.profit_total)}đ`,
	]);
	const buildExcel = () => [
		{name: 'Tổng quan', aoa: [['Chỉ số', 'Giá trị'], ...overviewRows]},
		{name: 'Gói', aoa: [['Gói', 'Giá', 'Người dùng', 'Doanh thu', 'Giá vốn/gói', 'Lãi/gói', 'Tổng lãi'], ...planRows]},
		{name: 'Top token', aoa: [['Thành viên', 'Token'], ...(top_token_users || []).map((r) => [r.name, r.value])]},
		{name: 'Top dự án', aoa: [['Thành viên', 'Dự án'], ...(top_project_users || []).map((r) => [r.name, r.value])]},
	];
	const buildPdf = () => ({
		title: 'Thống kê tổng quan hệ thống',
		subtitle: `Cập nhật ${dayjs(data.generated_at).format('HH:mm DD/MM/YYYY')}`,
		sections: [
			{heading: 'Tổng quan', columns: ['Chỉ số', 'Giá trị'], rows: overviewRows},
			{heading: 'Gói dịch vụ', columns: ['Gói', 'Giá', 'Người dùng', 'Doanh thu', 'Giá vốn/gói', 'Lãi/gói', 'Tổng lãi'], rows: planRows},
		],
	});

	// Số tiền lãi/lỗ — xanh khi dương, đỏ khi âm.
	const money = (v) => (
		<span style={{color: (v || 0) >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600}}>{nf(v)}đ</span>
	);

	const planColumns = [
		{title: 'Gói', key: 'name', render: (_, r) => (
			<span><Tag color={planColor(r.slug)}>{r.name}</Tag>{r.status === 'inactive' && <Tag>ẩn</Tag>}</span>
		)},
		{title: 'Giá / kỳ', dataIndex: 'price', key: 'price', align: 'right', render: (v) => v > 0 ? `${nf(v)}đ` : 'Miễn phí'},
		{title: 'Người dùng', dataIndex: 'subscribers', key: 'subscribers', align: 'center', render: (v) => nf(v)},
		{title: 'Doanh thu ước tính', dataIndex: 'revenue', key: 'revenue', align: 'right', render: (v) => `${nf(v)}đ`},
		{title: 'Giá vốn / gói', dataIndex: 'cost_vnd', key: 'cost', align: 'right', responsive: ['md'], render: (v) => `${nf(v)}đ`},
		{title: 'Lãi / gói', dataIndex: 'profit_vnd', key: 'profit', align: 'right', responsive: ['md'], render: (v, r) => (
			<span>{money(v)}{r.margin_pct !== null && r.margin_pct !== undefined && <span style={{color: '#9ca3af', fontSize: 12, marginLeft: 4}}>({r.margin_pct}%)</span>}</span>
		)},
		{title: 'Tổng lãi', dataIndex: 'profit_total', key: 'profit_total', align: 'right', responsive: ['md'], render: (v) => money(v)},
	];

	const topColumns = (label) => [
		{title: 'Thành viên', key: 'name', render: (_, r) => (
			<Link to={`/admin/members/${r.id}`}>{r.name}</Link>
		)},
		{title: label, dataIndex: 'value', key: 'value', align: 'right', render: (v) => nf(v)},
	];

	return (
		<div className="container">
			<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap'}}>
				<PageHeader
					icon="fa-light fa-chart-line"
					title="Thống kê tổng quan"
					subtitle={`Bức tranh toàn hệ thống · cập nhật ${dayjs(data.generated_at).format('HH:mm DD/MM/YYYY')}`}
				/>
				<ExportButtons filename="thong-ke-tong-quan" buildExcel={buildExcel} buildPdf={buildPdf} small={false} />
			</div>

			{/* Cảnh báo bất thường — mở rộng xem chi tiết các lần thất bại gần nhất */}
			{alerts?.login_fail_24h > 0 && (
				<Alert
					type="warning"
					showIcon
					style={{marginBottom: 16}}
					message={`${nf(alerts.login_fail_24h)} lượt đăng nhập thất bại trong 24 giờ qua (${nf(alerts.login_fail_7d)} trong 7 ngày). Kiểm tra dấu hiệu dò mật khẩu.`}
					description={recentFails.length > 0 && (
						<Collapse ghost size="small" items={[{
							key: 'fails',
							label: 'Xem các lần thất bại gần nhất',
							children: (
								<Table
									size="small"
									rowKey={(_, i) => i}
									pagination={false}
									columns={[
										{title: 'Tài khoản đã thử', dataIndex: 'username', render: (v) => v || <i style={{color: '#aaa'}}>—</i>},
										{title: 'IP', dataIndex: 'ip', width: 140, render: (v) => v || '—'},
										{title: 'Thời gian', dataIndex: 'created', width: 170},
									]}
									dataSource={recentFails}
									scroll={{x: 'max-content'}}
								/>
							),
						}]}
						/>
					)}
				/>
			)}

			{/* Hoạt động trong kỳ (chọn khoảng ngày + so kỳ liền trước) */}
			<div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'}}>
				<div className={cn('section-title')} style={{marginBottom: 0}}>Hoạt động trong kỳ</div>
				<DatePicker.RangePicker
					allowClear={false}
					value={range}
					onChange={(v) => v && setRange(v)}
					format="DD/MM/YYYY"
					presets={[
						{label: '7 ngày', value: [dayjs().subtract(6, 'day'), dayjs()]},
						{label: '30 ngày', value: [dayjs().subtract(29, 'day'), dayjs()]},
						{label: '90 ngày', value: [dayjs().subtract(89, 'day'), dayjs()]},
					]}
				/>
			</div>
			{activity && (
				<div style={{color: '#9ca3af', fontSize: 12, margin: '2px 0 10px'}}>
					So với kỳ liền trước ({dayjs(activity.prev_range.from).format('DD/MM')}–{dayjs(activity.prev_range.to).format('DD/MM')})
				</div>
			)}
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-user-plus" value={nf(am.new_members?.value)} label="Thành viên mới" delta={am.new_members?.delta_pct} />
				<StatCard icon="fa-light fa-wand-magic-sparkles" value={compact(am.ai_tokens?.value)} label="Token AI đã tiêu" delta={am.ai_tokens?.delta_pct} />
				<StatCard icon="fa-light fa-magnifying-glass-chart" value={nf(am.seo_reports?.value)} label="Báo cáo SEO" delta={am.seo_reports?.delta_pct} />
				<StatCard icon="fa-light fa-spider" value={nf(am.crawls?.value)} label="Lượt crawl" delta={am.crawls?.delta_pct} />
				<StatCard icon="fa-light fa-robot" value={nf(am.ai_history?.value)} label="Lượt sinh nội dung AI" delta={am.ai_history?.delta_pct} />
				<StatCard icon="fa-light fa-user-lock" value={nf(am.login_fails?.value)} label="Đăng nhập thất bại" delta={am.login_fails?.delta_pct} goodWhenUp={false} />
			</div>

			{/* Thành viên */}
			<div className={cn('section-title')}>Thành viên</div>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-users" value={nf(members.total)} label="Tổng thành viên" />
				<StatCard icon="fa-light fa-user-check" value={nf(members.active)} label="Đang hoạt động" />
				<StatCard icon="fa-light fa-user-lock" value={nf(members.blocked)} label="Bị khóa / tạm dừng" />
				<StatCard icon="fa-light fa-user-plus" value={nf(members.new_this_month)} label="Mới trong tháng" />
			</div>

			{/* Doanh thu & Gói — giá vốn/lãi tính theo "Tham số giá vốn" (màn Quản lý gói) */}
			<div className={cn('section-title')}>Doanh thu & Gói dịch vụ</div>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-sack-dollar" value={`${nf(plans?.revenue_monthly)}đ`} label="Doanh thu ước tính / tháng" />
				<StatCard icon="fa-light fa-receipt" value={`${nf(plans?.cost_monthly)}đ`} label="Giá vốn ước tính / tháng" />
				<StatCard icon="fa-light fa-chart-mixed-up-circle-dollar" value={`${nf(plans?.profit_monthly)}đ`} label="Lợi nhuận ước tính / tháng" />
			</div>
			<Table
				columns={planColumns}
				dataSource={plans?.items || []}
				rowKey="slug"
				pagination={false}
				size="middle"
				scroll={{x: 'max-content'}}
				style={{marginBottom: 24}}
			/>

			{/* Tài nguyên hệ thống */}
			<div className={cn('section-title')}>Tài nguyên hệ thống</div>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-folder-tree" value={nf(resources.projects)} label="Dự án" />
				<StatCard icon="fa-light fa-magnifying-glass-chart" value={nf(resources.seo_reports)} label="Báo cáo SEO" />
				<StatCard icon="fa-light fa-gauge-high" value={nf(resources.psi_reports)} label="Báo cáo PageSpeed" />
				<StatCard icon="fa-light fa-spider" value={nf(resources.crawls)} label="Lượt crawl" />
			</div>

			{/* Token & URL */}
			<div className={cn('section-title')}>AI Token & Kiểm tra URL</div>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-wand-magic-sparkles" value={compact(ai_tokens.used_this_month)} label="Token AI dùng trong tháng" />
				<StatCard icon="fa-light fa-coins" value={compact(ai_tokens.consumed_all_time)} label="Tổng token đã tiêu" />
				<StatCard icon="fa-light fa-link" value={nf(url_checks.used_this_week)} label="Kiểm tra URL trong tuần" />
				<StatCard icon="fa-light fa-robot" value={nf(resources.ai_history)} label="Lượt sinh nội dung AI" />
			</div>

			{/* Ngân sách tài khoản AI (khoá chung) — cần bao nhiêu tiền để hệ thống ổn định */}
			{ai_budget && (
				<>
					<div className={cn('section-title')}>Ngân sách tài khoản AI (khoá chung)</div>
					<div className={cn('stats')}>
						<StatCard icon="fa-light fa-coins" value={compact(ai_budget.committed_tokens)} label="Token cam kết / tháng (mọi gói)" />
						<StatCard icon="fa-light fa-fire" value={compact(ai_budget.used_tokens_month)} label="Đã dùng tháng này" />
						<StatCard icon="fa-light fa-battery-half" value={compact(ai_budget.remaining_tokens)} label="Còn phải dự phòng (token)" />
						<StatCard
							icon="fa-light fa-vault"
							value={`${nf(ai_budget.recommended_vnd)}đ`}
							label={`Cần giữ trong tài khoản AI (≈ $${nf(ai_budget.recommended_usd)})`}
						/>
					</div>
					<div style={{color: '#9ca3af', fontSize: 12, margin: '-8px 0 20px'}}>
						Tính theo đơn giá blended {nf(ai_budget.vnd_per_1m_tokens)}đ / 1M token (≈ ${ai_budget.usd_per_1m_tokens}/1M),
						đã cộng {nf(ai_budget.buffer_pct)}% dự phòng; gói bắt buộc khoá AI riêng không tính vào cam kết.
						Thực tiêu 30 ngày gần nhất: {compact(ai_budget.used_30d_tokens)} token ≈ {nf(ai_budget.cost_30d_vnd)}đ.
						Chỉnh đơn giá ở <Link to="/admin/plans">Quản lý gói → Tham số giá vốn</Link>.
					</div>
				</>
			)}

			{/* Top thành viên */}
			<div className={cn('section-title')}>Top thành viên dùng nhiều tài nguyên</div>
			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<Table
						title={() => <strong>Nhiều token AI nhất (tháng này)</strong>}
						columns={topColumns('Token')}
						dataSource={top_token_users || []}
						rowKey="id"
						pagination={false}
						size="small"
						scroll={{x: 'max-content'}}
						locale={{emptyText: 'Chưa có dữ liệu'}}
					/>
				</Col>
				<Col xs={24} lg={12}>
					<Table
						title={() => <strong>Nhiều dự án nhất</strong>}
						columns={topColumns('Dự án')}
						dataSource={top_project_users || []}
						rowKey="id"
						pagination={false}
						size="small"
						scroll={{x: 'max-content'}}
						locale={{emptyText: 'Chưa có dữ liệu'}}
					/>
				</Col>
			</Row>
		</div>
	);
}

export default AdminStats;
